import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { RemoteConfig, DEFAULT_CONFIG, DEFAULT_PLACEHOLDER_IMAGE } from '../models/interfaces/remote-config.interface';

/**
 * Remote Configuration Service
 * Fetches and manages server-side configuration for feature flags and settings
 */
@Injectable({
  providedIn: 'root'
})
export class RemoteConfigService {
  private http = inject(HttpClient);
  
  private readonly CONFIG_STORAGE_KEY = 'remote-config';
  private readonly CONFIG_TIMESTAMP_KEY = 'remote-config-timestamp';
  private readonly CACHE_DURATION_MS = 15 * 60 * 1000; // 15 minutes
  private readonly POLL_INTERVAL_MS = 15 * 1000; // 15 seconds for near-instant updates

  private configSubject = new BehaviorSubject<RemoteConfig>(DEFAULT_CONFIG);
  public config$ = this.configSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private pollTimer: any = null;
  private lastFetchedHash = '';
  
  /**
   * Initialize config service - load from cache, then fetch from server
   * Called by APP_INITIALIZER before app fully loads
   */
  async initialize(): Promise<void> {
    console.log('🔧 RemoteConfigService: Initializing...');
    
    try {
      // Step 1: Load from cache immediately (fast - no network delay)
      const cachedConfig = await this.loadFromCache();
      if (cachedConfig) {
        console.log('📦 RemoteConfigService: Loaded config from cache');
        this.configSubject.next(cachedConfig);
      } else {
        console.log('📦 RemoteConfigService: No valid cache found, using defaults');
        this.configSubject.next(DEFAULT_CONFIG);
      }
      
      // Step 2: Fetch fresh config from server (background update)
      console.log('🌐 RemoteConfigService: Fetching fresh config from server...');
      await this.fetchConfig();
      console.log('✅ RemoteConfigService: Fresh config fetched and saved');

      // Step 3: Start polling for near-instant config updates
      this.startPolling();
      
    } catch (error) {
      console.warn('⚠️ RemoteConfigService: Failed to initialize, using cached/default config', error);
      
      // Ensure we have at least the default config
      if (!this.configSubject.value) {
        this.configSubject.next(DEFAULT_CONFIG);
      }
    }
  }
  
  /**
   * Fetch configuration from server
   * Updates both the in-memory config and saves to Preferences
   */
  async fetchConfig(): Promise<RemoteConfig> {
    this.loadingSubject.next(true);
    
    try {
      const url = `${environment.apiUrl}/api/config/app`;
      const params = { 
        platform: environment.production ? 'mobile' : 'web',
        version: environment.appVersion 
      };
      
      console.log(`🌐 Fetching config from: ${url}`, params);
      
      // Fetch from server and handle errors
      const config = await this.http.get<RemoteConfig>(url, { params }).pipe(
        map((response: RemoteConfig) => {
          console.log('📥 Config response received:', response);
          return this.normalizeConfig(response);
        }),
        catchError((error: any) => {
          console.warn('⚠️ Failed to fetch config from server, using cached/default', error);
          return of(this.configSubject.value);
        })
      ).toPromise();
      
      // Save to cache and update in-memory config
      if (config) {
        console.log('💾 Saving config to Preferences...');
        const normalizedConfig = this.normalizeConfig(config);
        await this.saveToCache(normalizedConfig);
        console.log('✅ Config saved to Preferences');
        
        // Update in-memory config
        this.configSubject.next(normalizedConfig);
        return normalizedConfig;
      }
      
      // Fallback to current value
      return this.configSubject.value;
    } finally {
      this.loadingSubject.next(false);
    }
  }
  
  /**
   * Update remote configuration (admin only)
   */
  async updateConfig(updates: Partial<RemoteConfig>): Promise<void> {
    this.loadingSubject.next(true);

    try {
      const response = await this.http.put<{ message: string; config: RemoteConfig }>(
        `${environment.apiUrl}/api/config/app`,
        updates
      ).toPromise();

      if (response?.config) {
        const normalizedConfig = this.normalizeConfig(response.config);
        this.configSubject.next(normalizedConfig);
        await this.saveToCache(normalizedConfig);
        // Reset the poller so it doesn't immediately overwrite the just-saved config
        this.startPolling();
        console.log('✅ Config updated successfully');
      }
    } catch (error) {
      console.error('❌ Failed to update config:', error);
      throw error;
    } finally {
      this.loadingSubject.next(false);
    }
  }

  /**
   * Reset configuration to defaults (admin only)
   */
  async resetConfig(): Promise<void> {
    this.loadingSubject.next(true);
    
    try {
      const response = await this.http.post<{ message: string; config: RemoteConfig }>(
        `${environment.apiUrl}/api/config/app/reset`,
        {}
      ).toPromise();
      
      if (response?.config) {
        const normalizedConfig = this.normalizeConfig(response.config);
        this.configSubject.next(normalizedConfig);
        await this.saveToCache(normalizedConfig);
        console.log('🔄 Config reset to defaults');
      }
    } catch (error) {
      console.error('❌ Failed to reset config:', error);
      throw error;
    } finally {
      this.loadingSubject.next(false);
    }
  }
  
  /**
   * Get current config synchronously
   */
  getConfig(): RemoteConfig {
    return this.configSubject.value;
  }

  getPlaceholderImageUrl(): string {
    return this.resolvePlaceholderImageUrl(this.getConfig().ui?.placeholderImageUrl);
  }
  
  /**
   * Get specific feature flag
   */
  isFeatureEnabled(featurePath: string): boolean {
    const config = this.getConfig();
    const keys = featurePath.split('.');
    
    let value: any = config.features;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return false;
    }
    
    return value === true;
  }
  
  /**
   * Check if AI features are enabled
   */
  isAIEnabled(): boolean {
    return this.getConfig().features.ai.enabled;
  }
  
  /**
   * Check if specific messaging feature is enabled
   */
  isMessagingFeatureEnabled(feature: keyof RemoteConfig['features']['messaging']): boolean {
    return this.getConfig().features.messaging[feature] as boolean;
  }
  
  /**
   * Check if in maintenance mode
   */
  isMaintenanceMode(): boolean {
    return this.getConfig().maintenance.enabled;
  }
  
  /**
   * Get maintenance message
   */
  getMaintenanceMessage(): string {
    return this.getConfig().maintenance.message || 'The app is currently under maintenance. Please try again later.';
  }
  
  /**
   * Check if user role is allowed during maintenance
   */
  isRoleAllowedDuringMaintenance(role: string): boolean {
    const allowedRoles = this.getConfig().maintenance.allowedUserRoles;
    if (!allowedRoles || allowedRoles.length === 0) return false;
    return allowedRoles.includes(role);
  }
  
  /**
   * Save config to Platform Preferences (local storage)
   */
  private async saveToCache(config: RemoteConfig): Promise<void> {
    try {
      console.log('💾 Saving config to Preferences:', { 
        version: config.version, 
        timestamp: new Date().toISOString() 
      });
      
      localStorage.setItem(this.CONFIG_STORAGE_KEY, JSON.stringify(config));
      localStorage.setItem(this.CONFIG_TIMESTAMP_KEY, Date.now().toString());
      
      console.log('✅ Config successfully saved to Preferences');
    } catch (error) {
      console.warn('⚠️ Failed to save config to Preferences:', error);
    }
  }
  
  /**
   * Load config from Platform Preferences (local storage)
   */
  private async loadFromCache(): Promise<RemoteConfig | null> {
    try {
      console.log('📦 Loading config from Preferences...');
      
      const timestampResult = await { value: localStorage.getItem(this.CONFIG_TIMESTAMP_KEY) };
      const timestamp = timestampResult.value ? parseInt(timestampResult.value, 10) : 0;
      
      // Check if cache is still valid (15 minutes)
      const cacheAge = Date.now() - timestamp;
      const cacheAgeMinutes = Math.floor(cacheAge / 60000);
      
      if (cacheAge > this.CACHE_DURATION_MS) {
        console.log(`📦 Cache expired (${cacheAgeMinutes} minutes old), will fetch fresh`);
        return null;
      }
      
      const configResult = await { value: localStorage.getItem(this.CONFIG_STORAGE_KEY) };
      if (!configResult.value) {
        console.log('📦 No cached config found');
        return null;
      }
      
      const config = this.normalizeConfig(JSON.parse(configResult.value) as RemoteConfig);
      console.log(`✅ Loaded cached config (${cacheAgeMinutes} minutes old):`, {
        version: config.version,
        aiEnabled: config.features.ai.enabled,
        voiceMessages: config.features.messaging.showVoiceMessages
      });
      
      return config;
    } catch (error) {
      console.warn('⚠️ Failed to load config from Preferences:', error);
      return null;
    }
  }
  
  /**
   * Clear cached config from Preferences
   */
  async clearCache(): Promise<void> {
    console.log('🗑️ Clearing config cache from Preferences...');
    await localStorage.removeItem(this.CONFIG_STORAGE_KEY);
    await localStorage.removeItem(this.CONFIG_TIMESTAMP_KEY);
    console.log('✅ Config cache cleared');
  }
  
  /**
   * Force refresh config from server (clears cache first)
   */
  async refresh(): Promise<void> {
    console.log('🔄 Force refreshing config from server...');
    await this.clearCache();
    await this.fetchConfig();
    console.log('✅ Config refreshed');
  }

  private normalizeConfig(config: Partial<RemoteConfig> | null | undefined): RemoteConfig {
    return {
      version: config?.version ?? DEFAULT_CONFIG.version,
      lastUpdated: config?.lastUpdated ?? DEFAULT_CONFIG.lastUpdated,
      features: {
        ai: { ...DEFAULT_CONFIG.features.ai, ...config?.features?.ai },
        messaging: { ...DEFAULT_CONFIG.features.messaging, ...config?.features?.messaging },
        payments: { ...DEFAULT_CONFIG.features.payments, ...config?.features?.payments },
        bookings: { ...DEFAULT_CONFIG.features.bookings, ...config?.features?.bookings },
        social: { ...DEFAULT_CONFIG.features.social, ...config?.features?.social },
        notifications: { ...DEFAULT_CONFIG.features.notifications, ...config?.features?.notifications },
        maps: { ...DEFAULT_CONFIG.features.maps, ...config?.features?.maps },
      },
      ui: {
        ...DEFAULT_CONFIG.ui,
        ...config?.ui,
        placeholderImageUrl: this.resolvePlaceholderImageUrl(config?.ui?.placeholderImageUrl),
      },
      business: {
        ...DEFAULT_CONFIG.business,
        ...config?.business,
      },
      maintenance: {
        ...DEFAULT_CONFIG.maintenance,
        ...config?.maintenance,
      },
    };
  }

  private resolvePlaceholderImageUrl(value?: string | null): string {
    const trimmed = value?.trim();
    if (!trimmed) {
      return DEFAULT_PLACEHOLDER_IMAGE;
    }

    // Accept data URIs, blob/file URIs, protocol-relative URLs, absolute URLs,
    // and paths that already start with `assets/` or a leading slash.
    if (/^(data:|blob:|file:|https?:\/\/|\/\/|\/|assets\/)/i.test(trimmed)) {
      return trimmed;
    }

    // If the admin stored an uploads path or a path-like value, assume it's served by the backend.
    // Examples:
    // - uploads/images/123.jpg  -> http://api/uploads/images/123.jpg
    // - images/123.jpg          -> http://api/images/123.jpg
    if (trimmed.startsWith('uploads/') || trimmed.includes('/')) {
      return `${environment.apiUrl.replace(/\/$/, '')}/${trimmed.replace(/^\/+/, '')}`;
    }

    // Bare filename (e.g. "image.png") — assume it's an uploaded file available under /uploads/
    return `${environment.apiUrl.replace(/\/$/, '')}/uploads/${trimmed}`;
  }

  /**
   * Start polling the server for config changes every POLL_INTERVAL_MS
   */
  private startPolling(): void {
    this.stopPolling(); // Clear any existing timer
    this.pollTimer = setInterval(() => {
      this.fetchConfig().catch(() => { /* silent fail, keep using current config */ });
    }, this.POLL_INTERVAL_MS);
    console.log(`🔄 RemoteConfigService: Polling every ${this.POLL_INTERVAL_MS / 1000}s for config updates`);
  }

  /**
   * Stop the polling timer
   */
  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Force an immediate config refresh — call after saving config from admin
   */
  async refreshNow(): Promise<void> {
    console.log('🔄 RemoteConfigService: Forced refresh requested');
    await this.fetchConfig();
  }

  ngOnDestroy() {
    this.stopPolling();
  }
}
